<c-container class="mt-3">
  <c-row>
    <c-col>
      <h2>Duplicate Card Fingerprints</h2>
      <p class="page-hint">
        Cards stored against more than one player in this game. A fingerprint is the same for every
        copy of the same card number, so a shared fingerprint means one card is funding several
        accounts. Player details are only shown once you state a reason.
      </p>
    </c-col>
  </c-row>

  @if (isCrossGame) {
    <app-choose-your-game [message]="'Select a game to check for duplicate card fingerprints'"
                          (gameChosenEvent)="onGameChosen($event)"></app-choose-your-game>
  }

  @if (game) {
    <c-row class="mb-3">
      <c-col>
        <mat-card>
          <mat-card-header>
            <mat-card-title>Shared Cards in {{ game.ShortName || game.Name }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            @if (uiState === uiStateForTemplate.ShowLoading) {
              <mat-spinner class="center-margin" data-testid="duplicate-fingerprints-loading"></mat-spinner>
            } @else if (duplicateGroups.length === 0) {
              <p data-testid="duplicate-fingerprints-empty-message">
                No card is shared between players in this game.
              </p>
            } @else {
              @if (resultsTruncated) {
                <p class="results-truncated-message" data-testid="duplicate-fingerprints-truncated-message">
                  Showing the first {{ maxResultsForTemplate }} shared cards only.
                </p>
              }
              <table mat-table [dataSource]="duplicateGroups" data-testid="duplicate-fingerprints-table">
                <ng-container matColumnDef="cardFingerprint">
                  <th mat-header-cell *matHeaderCellDef>Card Fingerprint</th>
                  <td mat-cell *matCellDef="let group">{{ group.CardFingerprint }}</td>
                </ng-container>

                <ng-container matColumnDef="cardLastFourDigits">
                  <th mat-header-cell *matHeaderCellDef>Last 4 Digits</th>
                  <td mat-cell *matCellDef="let group">{{ group.CardLastFourDigits }}</td>
                </ng-container>

                <ng-container matColumnDef="playerCount">
                  <th mat-header-cell *matHeaderCellDef>Players</th>
                  <td mat-cell *matCellDef="let group" class="bold-text">{{ group.PlayerCount }}</td>
                </ng-container>

                <ng-container matColumnDef="paymentMethodCount">
                  <th mat-header-cell *matHeaderCellDef>Payment Methods (Active)</th>
                  <td mat-cell *matCellDef="let group">
                    {{ group.PaymentMethodCount }} ({{ group.ActivePaymentMethodCount }})
                  </td>
                </ng-container>

                <ng-container matColumnDef="lastSeenOn">
                  <th mat-header-cell *matHeaderCellDef>Last Added</th>
                  <td mat-cell *matCellDef="let group">{{ group.LastSeenOn | date: 'medium' }}</td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef></th>
                  <td mat-cell *matCellDef="let group">
                    <button mat-raised-button color="primary"
                            (click)="onViewPlayersClick(group)"
                            attr.data-testid="view-players-button-{{ group.CardFingerprint }}">
                      View Players
                    </button>
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="groupColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: groupColumns;" class="table-row"></tr>
              </table>
            }
          </mat-card-content>
        </mat-card>
      </c-col>
    </c-row>

    @if (selectedFingerprint) {
      <c-row>
        <c-col>
          <mat-card>
            <mat-card-header>
              <mat-card-title>Players Sharing {{ selectedFingerprint }}</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              @if (playersUiState === uiStateForTemplate.ShowLoading) {
                <mat-spinner class="center-margin"></mat-spinner>
              } @else {
                <table mat-table [dataSource]="selectedFingerprintPlayers" data-testid="duplicate-fingerprint-players-table">
                  <ng-container matColumnDef="playerEmail">
                    <th mat-header-cell *matHeaderCellDef>Player</th>
                    <td mat-cell *matCellDef="let player">
                      @if (canViewPlayerInfo) {
                        <a [routerLink]="getPlayerManageUrl(player.PlayerId)">{{ player.PlayerEmail }}</a>
                      } @else {
                        <span data-testid="duplicate-fingerprint-player-email-text">{{ player.PlayerEmail }}</span>
                      }
                    </td>
                  </ng-container>

                  <ng-container matColumnDef="cardLastFourDigits">
                    <th mat-header-cell *matHeaderCellDef>Last 4 Digits</th>
                    <td mat-cell *matCellDef="let player">{{ player.CardLastFourDigits }}</td>
                  </ng-container>

                  <ng-container matColumnDef="paymentMethodCount">
                    <th mat-header-cell *matHeaderCellDef>Payment Methods</th>
                    <td mat-cell *matCellDef="let player">{{ player.PaymentMethodCount }}</td>
                  </ng-container>

                  <ng-container matColumnDef="hasActivePaymentMethod">
                    <th mat-header-cell *matHeaderCellDef>Card Active</th>
                    <td mat-cell *matCellDef="let player">{{ player.HasActivePaymentMethod ? 'Yes' : 'No' }}</td>
                  </ng-container>

                  <ng-container matColumnDef="playerActive">
                    <th mat-header-cell *matHeaderCellDef>Player Status</th>
                    <td mat-cell *matCellDef="let player" data-testid="duplicate-fingerprint-player-status"
                        [class]="player.PlayerActive ? 'green-text' : 'red-text'">
                      {{ player.PlayerActive ? 'Active' : 'Deactivated' }}
                    </td>
                  </ng-container>

                  <ng-container matColumnDef="firstSeenOn">
                    <th mat-header-cell *matHeaderCellDef>First Added</th>
                    <td mat-cell *matCellDef="let player">{{ player.FirstSeenOn | date: 'medium' }}</td>
                  </ng-container>

                  <tr mat-header-row *matHeaderRowDef="playerColumns"></tr>
                  <tr mat-row *matRowDef="let row; columns: playerColumns;" class="table-row"></tr>
                </table>

                <button mat-raised-button class="margin-top-med" (click)="onClosePlayersClick()"
                        data-testid="close-duplicate-fingerprint-players-button">
                  Close
                </button>
              }
            </mat-card-content>
          </mat-card>
        </c-col>
      </c-row>
    }
  }
</c-container>
